import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Breadcrumbs } from '~/components/Breadcrumps';
import { Button } from '~/components/Button';
import { Trash2 } from "lucide";
import { LucideIcon } from "~/components/LucideIcon";
import { useConfirm } from '~/components/Dialogs';
import { onSubmitError } from '~/components/Form';
import { useModals } from '~/components/Modal';
import { Progress } from '~/components/Progress';
import { TableRowActions, TableSort, TableSortDirection } from '~/components/Table';
import { ToggleGroup } from '~/components/Controls/ToggleGroup';
import { Title } from '~/components/Title';
import { useAbility } from '~/lib/ability';
import { CSV } from '~/lib/csv';
import { debounce } from 'lodash';
import { useDataSource } from '~/lib/dataSource';
import { renderBoolean, renderToggle } from '~/lib/helpers';
import { Intent } from '~/lib/intent';
import { useResource, controllableUseResource } from '~/lib/loader';
import { sortByNumber} from '~/lib/sort';
import { getApiErrorMessage } from '~/lib/errors/apiError';
import { clientsLinks } from '~/modules/Clients';
import { ClientsAbility } from '~/modules/Clients/ability';
import { sellersAdapter } from '~/modules/Clients/adapters';
import {
    bulkAssignSellerPreset,
    createConfigs,
    createGlobalSellersConfig,
    deleteConfigs,
    getGlobalSellersConfig,
    fetchSeller,
    toggleSeller,
    updateConfigs,
    fetchSellers
} from '~/modules/Clients/api';
import { ClientsTypeNavigation } from '~/modules/Clients/components/ClientsTypeNavigation';
import { ConfigsManipulationForm } from '~/modules/Clients/forms/ConfigsManipulationForm';
import { GlobalSellerConfigForm } from '~/modules/Clients/forms/GlobalSellerConfigForm';
import { PresetBulkAssignForm } from '~/modules/Clients/forms/PresetBulkAssignForm';
import { SellerForm } from '~/modules/Clients/forms/SellerForm';
import { SellerType } from '~/modules/Clients/models/AccountInfo';
import { Operation } from '~/modules/Clients/models/ConfigsManipulation';
import { defaultSeller } from '~/modules/Clients/models/Seller';
import { paymentsLinks } from '~/modules/Payments/links';
import { Pagination } from '~/components/Pagination';
import { Input } from "~/components/Controls/Input";
import { SelectAccountManager } from '~/modules/Clients/components/SelectAccountManager';
import { useAccountManagersQuery } from '~/modules/Dictionary/queries';
import style from './index.module.scss';

export const SellersView = () => {
    const {state, createRecord, updateRecord, deleteRecord} = useDataSource(sellersAdapter);
    const globalConfigResult = useResource(() => getGlobalSellersConfig());
    const {data: accountManagers = [], isPending: isAccountManagersPending} = useAccountManagersQuery();
    const confirm = useConfirm();
    const modal = useModals();
    const navigate = useNavigate();
    const ability = useAbility(ClientsAbility);
    const [paginator, setPaginator] = useState<{ page: number; pageSize: number }>({page: 0, pageSize: 25});
    const [sort, setSort] = useState({field: 'id', direction: 'desc'});
    const [filter, setFilter] = useState({
        id: undefined,
        active: undefined,
        name: '',
        sellerType: SellerType.INTERNAL,
        accountManagerId: undefined,
    });
    const [searchName, setSearchName] = useState('');

    const accountManagerById = useMemo(() => {
        const map = new Map<number, string>();
        accountManagers.forEach((m) => {
            if (m?.id != null) map.set(m.id, m.name);
        });
        return map;
    }, [accountManagers]);

    const debouncedSearch = useCallback(
        debounce((value) => {
            setFilter(prev => ({ ...prev, name: value }));
            setPaginator({ page: 0, pageSize: 25 });
        }, 600),
        []
    );

    const handleSearchNameChange = (value) => {
        setSearchName(value);
        if (/^\d+$/.test(value)) {
            setFilter(prev => ({
                ...prev,
                id: value,
                name: '',
            }));
        } else {
            setFilter(prev => ({
                ...prev,
                name: value,
                id: undefined,
            }));
        }
        setPaginator({ page: 0, pageSize: 25 });
    };

    const handleActiveChange = (value) => {
        setPaginator(prev => ({ ...prev, page: 0 }));
        setFilter(prev => ({ ...prev, active: value }));
    };

    const handleAccountManagerChange = (value) => {
        setPaginator(prev => ({ ...prev, page: 0 }));
        setFilter(prev => ({ ...prev, accountManagerId: value ?? undefined }));
    };

    const toggleSelfServe = () => {
        setPaginator(prev => ({ ...prev, page: 0 }));
        setFilter(prev => ({
            ...prev,
            sellerType: prev.sellerType === SellerType.INTERNAL
                ? SellerType.SELF_SERVE
                : SellerType.INTERNAL,
        }));
    };

    const result = controllableUseResource(() => fetchSellers(
        filter,
        {
            pageSize: paginator.pageSize,
            pageNo: paginator.page,
            ...(sort.field === 'id' ? {sortBy: sort.direction} : {})
        }
    ), {items: [], count: 0, page: 1, pageSize: 0});
    const activeOptions = [
        { label: 'All', value: undefined },
        { label: 'Active', value: true },
        { label: 'Inactive', value: false },
    ];

    useEffect(() => {
        result.fetch();
        return () => debouncedSearch.cancel();
    }, [filter.active, filter.sellerType, filter.name, filter.id, filter.accountManagerId, paginator.page, paginator.pageSize, sort.field, sort.direction]);

    const editSellerForm = modal.create(SellerForm, {
        onSubmit: (seller, formikActions, close) => {
            updateRecord(seller).then(() => {
                setPaginator({ page: 0, pageSize: 25 });
                close();
            }).catch(onSubmitError(formikActions));
        }
    });

    const createSellerForm = modal.create(SellerForm, {
        onSubmit: (seller, formikActions, close) => {
            createRecord(seller).then(close).catch(onSubmitError(formikActions));
        }
    });

    const presetBulkAssignForm = modal.create(PresetBulkAssignForm, {
        side: 'supply',
        onSubmit: ({resourceIds, presetId}, formActions, close) => {
            if (presetId == null) return;
            bulkAssignSellerPreset(resourceIds, presetId)
                .then(() => {
                    close();
                    result.fetch();
                })
                .catch(error => formActions.setStatus(getApiErrorMessage(error, 'Failed to assign preset')));
        }
    });

    const configsManipulationForm = modal.create(ConfigsManipulationForm, {
        onSubmit: ({ sellerIds, configuration, operation, target, keys }, formikActions, close) => {
            const handleError = (error: any) => {
                formikActions.setSubmitting(false);
                const responseData = error?.response?.data;
                if (typeof responseData === 'string') {
                    formikActions.setStatus(responseData);
                    return;
                }
                if (responseData && typeof responseData.message === 'string') {
                    formikActions.setStatus(responseData.message);
                    return;
                }
                formikActions.setStatus(error?.message || 'Request failed');
            };

            return Promise.resolve()
                .then(() => {
                    if (operation === Operation.CREATE) {
                        return createConfigs(target, sellerIds, configuration);
                    }
                    if (operation === Operation.UPDATE) {
                        return updateConfigs(target, sellerIds, configuration);
                    }
                    if (operation === Operation.DELETE) {
                        return deleteConfigs(target, sellerIds, keys);
                    }
                })
                .then(close)
                .catch(handleError);
        }
    });

    const globalConfigForm = modal.create(GlobalSellerConfigForm, {
        configuration: globalConfigResult.resource,
        onSubmit: (configuration, formikActions, close) => {
            createGlobalSellersConfig({
                ...configuration,
                config: JSON.parse(configuration.config)
            }).then(globalConfigResult.fetch).then(close).catch(onSubmitError(formikActions));
        }
    });

    return <div className={style.sellerList}>
        <div className="page-header-sticky">
            <div className="page-header-breadcrumbs">
                <Breadcrumbs/>
            </div>
            <Title className="page-header-title" toolbar={<>
                {ability.canEditGlobalSellerConfig() &&
                    <Button key='global-config' responsive disabled={state.isPending} intent={Intent.SUCCESS}
                        leftIcon={<i className='fa fa-globe'/>}
                        onClick={() => globalConfigForm.show()}>Global config</Button>}
                {ability.canBulkUpdateConfigs() &&
                    <Button key='configs' responsive disabled={state.isPending} intent={Intent.SUCCESS}
                        leftIcon={<i className='fa fa-table'/>}
                        onClick={() => configsManipulationForm.show()}>Configs</Button>}
                {ability.canBulkAssignSellerPreset() &&
                    <Button key='bulk-preset' responsive disabled={result.isPending} intent={Intent.SUCCESS}
                        leftIcon={<i className='fa fa-table'/>}
                        onClick={() => presetBulkAssignForm.show()}>Preset</Button>}
                {ability.canCreateSeller() &&
                    <Button key='create' responsive disabled={state.isPending} intent={Intent.PRIMARY}
                        leftIcon={<i className='fa fa-plus'/>}
                        onClick={() => createSellerForm.show({seller: defaultSeller})}>Create</Button>}
                <Button key='reload' responsive disabled={result.isPending} intent={Intent.SUCCESS}
                    leftIcon={<i className='fas fa-sync-alt'/>}
                    onClick={() => result.fetch()}
                >Reload</Button>
                <Button responsive disabled={state.isPending}
                    onClick={() => CSV.fromDataSource(state.records, ['id', 'name', 'active']).download('sellers.csv')}
                    leftIcon={<i className='far fa-file-excel'/>}
                >Export</Button>
            </>}>Sellers</Title>
        </div>
        <ClientsTypeNavigation/>
        <div className="layout-content-box">
            <div className={style.filters}>
                <Input
                    value={searchName}
                    onChange={handleSearchNameChange}
                    placeholder="Search by name or ID"
                    rightIcon={<i className="fa fa-search input-search-icon"/>}
                />
                <ToggleGroup
                    ariaLabel="Status"
                    items={activeOptions}
                    value={filter.active}
                    onChange={handleActiveChange}
                    disabled={result.isPending}
                />
                <div style={{minWidth: 160}}>
                    <SelectAccountManager
                        value={filter.accountManagerId ?? null}
                        placeholder="Account Manager"
                        onChange={handleAccountManagerChange}
                        clearable
                    />
                </div>
                <button
                    type="button"
                    className={style.selfServeToggleButton}
                    disabled={state.isPending}
                    onClick={toggleSelfServe}
                >
                    <i
                        className={[
                            "far",
                            filter.sellerType === SellerType.SELF_SERVE ? "fa-toggle-on" : "fa-toggle-off",
                            filter.sellerType === SellerType.SELF_SERVE
                                ? style.selfServeToggleIconOn
                                : style.selfServeToggleIconOff,
                        ].join(" ")}
                        aria-hidden="true"
                    />
                    <span className={style.selfServeToggleLabel}>Self-serve</span>
                </button>
            </div>
            <TableSort records={(result.resource.items || [])}
            header
            defaultSort={{
                sort: sortByNumber,
                field: 'id',
                direction: TableSortDirection.DESC
            }}
            columns={[{
                header: 'ID',
                small: true,
                field: 'id',
                backendSort: true,
                sort: sortByNumber
            }, {
                field: 'name',
                header: 'Name',
                cell: seller => <NavLink to={clientsLinks.seller(seller.id)}>{seller.name}</NavLink>
            }, {
                header: 'Account Manager',
                field: 'accountManagerId',
                cell: (seller) => seller.accountManagerId != null ? (isAccountManagersPending ? null : accountManagerById.get(seller.accountManagerId) ?? seller.accountManagerId) : null,
            }, {
                field: 'active',
                header: 'Active',
                small: true,
                cell: seller => ability.canEditSeller(seller) ? renderToggle(seller.active,
                    () => confirm(seller.active ? 'Deactivate seller?' : 'Activate seller?', {
                        onOk: () => toggleSeller(seller, !seller.active).then(() => result.fetch()),
                        icon: <i className='fa fa-question' />,
                        intent: Intent.WARNING
                    })) : renderBoolean(seller.active)
            }, {
                small: true,
                cell: (seller) => (
                    <TableRowActions>
                        <Button
                            small
                            variant="menu"
                            leftIcon={<i className='fa fa-dollar' />}
                            onClick={() => navigate(paymentsLinks.payments(seller.id))}
                        >
                            Payments
                        </Button>
                        {ability.canEditSeller(seller) && (
                            <Button
                                small
                                variant="menu"
                                leftIcon={<i className='fa fa-edit' />}
                                onClick={async () => {
                                    let fullSeller = await fetchSeller(seller.id);
                                    editSellerForm.show({ seller: fullSeller })
                                }}
                            >
                                Edit
                            </Button>
                        )}
                        {ability.canDeleteSeller(seller) && (
                            <Button
                                small
                                variant="menu"
                                intent={Intent.DANGER}
                                onClick={() => confirm('Delete seller?', {
                                    onOk: () => deleteRecord(seller),
                                    icon: <LucideIcon icon={Trash2} size={20} />,
                                    intent: Intent.DANGER
                                })}
                                leftIcon={<i className='fa fa-trash' />}
                            >
                                Delete
                            </Button>
                        )}
                    </TableRowActions>
                )
            }]}
            onSortChange={({ field, direction, backendSort }) => {
                if (backendSort) {
                    setSort({ field, direction: direction === TableSortDirection.ASC ? 'asc' : 'desc' });
                    setPaginator({ page: 0, pageSize: paginator.pageSize });
                }
            }}
            placeholder={(result.isPending || globalConfigResult.isPending) && <Progress />}
            />

            {!result.isPending &&
                <Pagination padding={4} offset={result.resource.pageSize * result.resource.page} limit={result.resource.pageSize}
                    total={result.resource.count} onChange={(page, perPage) => {
                        setPaginator({ page, pageSize: perPage });
                    }} />}
        </div>
    </div>;
};
